import type { OrgRole, PlatformRole } from './roles.js';

/** Actions that require a platform-level role. */
export const PLATFORM_ACTIONS = [
  'platform.users.manage',
  'platform.invite.projectAdmin',
  'platform.audit.read',
  /** Organizations overview in Administration (owner, member/project counts) - never org content. */
  'platform.orgs.readAll',
  'org.create',
] as const;
export type PlatformAction = (typeof PLATFORM_ACTIONS)[number];

/** Actions scoped to one organization. */
export const ORG_ACTIONS = [
  'org.read',
  'org.update',
  'org.members.read',
  'org.members.invite',
  'org.members.manage',
  'project.read',
  'project.create',
  'project.update',
  'project.delete',
  /** Assign Project Members to projects. */
  'project.members.manage',
  'test.read',
  'test.write',
  /** Change or delete tests and test data created by someone else, and delete their run reports. */
  'test.manage.any',
  'run.trigger',
  'run.read',
  /** Upload app builds (mobile projects); delete your own (others' with test.manage.any). */
  'build.write',
  /** Connect phones through a 1QA Device Agent; manage your own agents (others' with test.manage.any). */
  'device.connect',
  /** Start performance tests (load) within the load limits (packages/shared/src/perf.ts checkLoad). */
  'perf.run',
  /** Connect 1QA Load Agents; manage your own (others' with test.manage.any). */
  'load.connect',
  /** Start security scans against the project's authorized Scope (packages/shared/src/security.ts). */
  'security.run',
  /** Set a project's security Scope: authorized hosts, allow-production, and the authorization attestation. */
  'security.scope.manage',
  'report.read.all',
  'report.share',
  'report.delete',
  /** Add organization-wide test data; change/delete only your own (others' with test.manage.any). */
  'variables.org.manage',
  /** AI models for the organization or one of its projects. */
  'llm.manage',
  /** Plugins for the organization or one of its projects. */
  'plugins.manage',
] as const;
export type OrgAction = (typeof ORG_ACTIONS)[number];

const PLATFORM_MATRIX: Record<PlatformRole, readonly PlatformAction[]> = {
  ADMIN: ['platform.users.manage', 'platform.invite.projectAdmin', 'platform.audit.read', 'platform.orgs.readAll'],
  PROJECT_ADMIN: ['org.create'],
  USER: [],
};

const ORG_MATRIX: Record<OrgRole, readonly OrgAction[]> = {
  PROJECT_ADMIN: ORG_ACTIONS,
  // Members only reach the projects they are assigned to (enforced by the API via ProjectMember).
  MEMBER: [
    'org.read',
    'org.members.read',
    'project.read',
    'test.read',
    'test.write',
    'run.trigger',
    'run.read',
    'build.write',
    'device.connect',
    'perf.run',
    'load.connect',
    // security.run yes (Members scan within the project's authorized Scope); security.scope.manage is Project Admin only.
    'security.run',
    'report.read.all',
    'report.share',
    'report.delete',
    'variables.org.manage',
  ],
  // Viewers only see reports shared with them (enforced by the API via ReportShare).
  VIEWER: ['org.read'],
};

export function canPlatform(role: PlatformRole, action: PlatformAction): boolean {
  return PLATFORM_MATRIX[role].includes(action);
}

/**
 * Org-scoped check. The platform ADMIN is not part of any organization or project: they only use the
 * Administration area (users, Project Admins, invitations, audit), so every org action is denied.
 */
export function canOrg(
  platformRole: PlatformRole,
  orgRole: OrgRole | null | undefined,
  action: OrgAction,
): boolean {
  if (platformRole === 'ADMIN') return false;
  if (!orgRole) return false;
  return ORG_MATRIX[orgRole].includes(action);
}

/**
 * Change or delete something with an owner (a test, test data): needs `action`, and either being
 * its creator or having test.manage.any (Project Admin). Everyone else can make a copy.
 */
export function canModifyOwned(
  platformRole: PlatformRole,
  orgRole: OrgRole | null | undefined,
  action: OrgAction,
  userId: string,
  ownerId: string,
): boolean {
  if (!canOrg(platformRole, orgRole, action)) return false;
  return userId === ownerId || canOrg(platformRole, orgRole, 'test.manage.any');
}

/**
 * Which projects of an org someone reaches: all (Project Admin), only the ones they are assigned to
 * (Project Member), or none (View Only - shared reports only; Admin - no org access).
 */
export type ProjectScope = 'all' | 'assigned' | 'none';
export function projectScope(platformRole: PlatformRole, orgRole: OrgRole | null | undefined): ProjectScope {
  if (platformRole === 'ADMIN') return 'none';
  if (orgRole === 'PROJECT_ADMIN') return 'all';
  if (orgRole === 'MEMBER') return 'assigned';
  return 'none';
}

/** Which org roles a given org role may assign when inviting or changing a member's role. */
export function assignableOrgRoles(platformRole: PlatformRole, orgRole: OrgRole | null): OrgRole[] {
  // Project Admins come from the platform Admin; inside an org they add members and viewers.
  if (platformRole !== 'ADMIN' && orgRole === 'PROJECT_ADMIN') return ['MEMBER', 'VIEWER'];
  return [];
}

/** A Project Admin creates exactly one organization. */
export const MAX_ORGS_PER_PROJECT_ADMIN = 1;
